import {
  Button,
  FormControl,
  Input,
  MessageBlock,
  Paragraph,
} from '@deckee/deck-hand';
import classNames from 'classnames/bind';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  afterLogout,
  callEndpoint,
  logOutOfOneSignal,
  telemetry,
} from '../../../../services/backend';
import { captureEventAsync } from '../../../../services/event-capture';
import { ROUTE_ACCOUNT_DELETED } from '../../../../services/route/route-names';
import {
  transitionToNextRoute,
  transitionToPreviousRoute,
} from '../../../../services/route/route-navigation';
import { selectCurrentMember } from '../../../../services/store/modules/backend-service';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../../layouts/components/LayoutBody/LayoutBody';
import styles from './DeleteAccountPage.scss';

const cx = classNames.bind(styles);

const DeleteAccountPage = () => {
  const currentMember = useSelector(selectCurrentMember);
  const [isLoading, setIsLoading] = useState(false);
  const [emailValidation, setEmailValidation] = useState('');
  const [error, setError] = useState<{ message: string } | null>(null);

  const formIsValid = emailValidation === currentMember?.email;

  const onSubmit = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      try {
        await callEndpoint(callEndpoint.endpoints.deleteAccount);
      } catch (err) {
        telemetry(
          // eslint-disable-next-line no-bitwise
          telemetry.ERROR | telemetry.NOTIFY_SYSTEM_CHANNEL,
          `[DeleteAccountPage.onSubmit] error deleting profile ${JSON.stringify(
            err,
          )}`,
        );
        setError({
          message: 'There was a problem deleting your profile.',
        });
        return;
      }

      const capturePromise = captureEventAsync({
        type: 'profile: delete',
        payload: {},
        sendToAmplitude: true,
      });

      transitionToNextRoute(ROUTE_ACCOUNT_DELETED, {}, true);

      try {
        // await this before we clear the user state
        await capturePromise;
      } catch (err) {
        telemetry(
          telemetry.ERROR,
          `[DeleteAccountPage.onSubmit] error capturing delete event ${JSON.stringify(
            err,
          )}`,
        );
      }

      try {
        await logOutOfOneSignal();
        await afterLogout();
      } catch (err) {
        telemetry(
          telemetry.ERROR,
          `[DeleteAccountPage.onSubmit] error during post-delete logout ${JSON.stringify(
            err,
          )}`,
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <BasicDrawerLayout
      heading="Delete account"
      onBack={() => transitionToPreviousRoute()}
      loading={isLoading}
      showMenuBar={false}
    >
      <LayoutBody>
        {error && (
          <MessageBlock type="error" mb="3">
            {error.message}
          </MessageBlock>
        )}
        <FormControl>
          <Paragraph size="-1" mb="2">
            To confirm the permanent deletion of your account, please retype
            your email address:
          </Paragraph>
          <Paragraph
            className={cx('delete-account__email')}
            textAlign="center"
            fontWeight="bold"
            size="2"
            mb="3"
          >
            {currentMember?.email}
          </Paragraph>
          <Input
            type="text"
            name="email"
            value={emailValidation}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmailValidation(e.target.value)
            }
            mb="3"
          />
        </FormControl>

        <Button.Group wide>
          <Button onClick={onSubmit} disabled={isLoading || !formIsValid}>
            Delete account
          </Button>
        </Button.Group>
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

export default DeleteAccountPage;
